import { MutableState } from 'src/replicant/State';
import {
  buddiesGetCommonProps,
  buddiesGetEventRewardDetails,
  buddiesGetScoreByPuzzleAttempts,
  buddiesGetUnseenPartyJoinCount,
  buddiesIsEventCompleted,
  buddiesIsDummyPlayer,
  buddiesIsPartyFull,
  buddiesIsAvailable,
} from './buddies.getters';
import type { SyncActionAPI } from '../utils/types';
import { sendAnalyticsEvents } from '../analytics';
import {
  BuddiesStatus,
  buddiesEventExpirationTime,
  buddiesSchedule,
} from './buddies.ruleset';
import { BuddiesEvent, BuddiesState } from './buddies.schema';

/**
 * Retrieve the buddies state from the state object, creating it if it does not exist
 */
function buddiesEnsureState(state: MutableState): BuddiesState {
  if (!state.bakingBuddies) {
    state.bakingBuddies = {
      events: {},
    };
  }
  return state.bakingBuddies;
}

/**
 * Retrieve the event state, creating it if it does not exist
 */
function buddiesEnsureEvent(state: MutableState, id: string): BuddiesEvent {
  const buddies = buddiesEnsureState(state);
  if (!buddies.events[id]) {
    buddies.events[id] = {
      party: {},
      invites: {},
      score: 0,
      seenAt: 0,
      nudgesSentCount: 0,
      rewardsClaimed: false,
    };
  }
  return buddies.events[id];
}

/**
 * Add a sent or received invite to the current event
 */
function buddiesAddOrUpdateInvite(
  state: MutableState,
  args: { id: string; now: number; status: BuddiesStatus },
) {
  // Can only be added if the request time is within the current event
  const schedule = buddiesSchedule.getCurrentEvent(args.now);
  if (!schedule) return;

  // Ensure the event state, based on the start time of the event
  const event = buddiesEnsureEvent(state, schedule.id);

  // Add the invite to the received list, or just update the timestamp if it already exists
  if (event.invites[args.id]) {
    event.invites[args.id].status = args.status;
    event.invites[args.id].updatedAt = args.now;
  } else {
    event.invites[args.id] = {
      score: 0,
      status: args.status,
      updatedAt: args.now,
    };
  }
}

/**
 * Mark event last seen at
 */
export function buddiesEventSeenAt(state: MutableState, now: number): void {
  const schedule = buddiesSchedule.getCurrentEvent(now);
  if (!schedule) return;
  const event = buddiesEnsureEvent(state, schedule.id);
  event.seenAt = now;
  // Keep the first introduction outside the event map, which expires.
  buddiesEnsureState(state).introducedAt ??= now;
}

/**
 * Add an invite sent to the current event
 */
export function buddiesAddInviteSent(
  state: MutableState,
  args: { id: string; now: number },
): void {
  buddiesAddOrUpdateInvite(state, { ...args, status: 'inviteSent' });
}

/**
 * Add an invite received to the current event
 */
export function buddiesAddInviteReceived(
  state: MutableState,
  args: { id: string; now: number },
): void {
  buddiesAddOrUpdateInvite(state, { ...args, status: 'inviteReceived' });
}

/**
 * Reject a received invite from the current event
 */
export function buddiesRemoveInvite(
  state: MutableState,
  args: { id: string; now: number },
): void {
  const schedule = buddiesSchedule.getCurrentEvent(args.now);
  if (!schedule) return;
  const event = buddiesEnsureEvent(state, schedule.id);
  delete event.invites[args.id];
}

/**
 * Add a player to the current event party, if party is not full
 */
export function buddiesAddPlayerToParty(
  state: MutableState,
  args: { id: string; now: number },
): void {
  const schedule = buddiesSchedule.getCurrentEvent(args.now);
  if (!schedule) return;

  const event = buddiesEnsureEvent(state, schedule.id);

  // Skip if the party is full
  if (buddiesIsPartyFull(state, args.now)) return;

  // Remove the invite from the received and sent lists, if it exists
  delete event.invites[args.id];

  // Add the friend if they are not already in the party
  if (!event.party[args.id]) {
    event.party[args.id] = {
      score: 0,
      updatedAt: args.now,
      status: 'active',
    };
  }
}

/**
 * Increment score of the main player in the current event
 */
export function buddiesAddMainPlayerScore(
  state: MutableState,
  args: { score: number; now: number },
): void {
  const schedule = buddiesSchedule.getCurrentEvent(args.now);
  if (!schedule) return;
  const event = buddiesEnsureEvent(state, schedule.id);
  event.score += args.score;
  event.lastScoreAdded = (event.lastScoreAdded ?? 0) + args.score;
}

/**
 * Add buddies score based on puzzle attempts (2 points for first attempt, 1 otherwise).
 * Sends analytics, notifies party members, and cancels invites if grand prize is reached.
 */
export function buddiesAddScoreByPuzzleAttempts(
  state: MutableState,
  args: { puzzleAttempts: number; now: number },
  api: SyncActionAPI,
): void {
  // Check if buddies is available for the user
  if (!buddiesIsAvailable(state, args.now)) return;

  // Ensure there is an active event (creates state if player hasn't visited buddies yet)
  const schedule = buddiesSchedule.getCurrentEvent(args.now);
  if (!schedule) return;
  const event = buddiesEnsureEvent(state, schedule.id);

  const score = buddiesGetScoreByPuzzleAttempts(args.puzzleAttempts);
  const before = buddiesGetCommonProps(state, args.now);

  buddiesAddMainPlayerScore(state, { score, now: args.now });

  // Track BuddiesMilestoneReach if milestone increased
  const after = buddiesGetCommonProps(state, args.now);
  if (after.milestone > before.milestone) {
    sendAnalyticsEvents(api, [
      { eventType: 'BuddiesMilestoneReach', eventProperties: after },
    ]);
  }

  // Notify party members about the score update
  for (const id in event.party) {
    if (buddiesIsDummyPlayer(id)) continue;
    api.postMessage.buddiesAddPartyPlayerScore(id, { score });
  }

  // If grand prize just reached, cancel all pending invites
  if (buddiesIsEventCompleted(state, args.now)) {
    for (const id in event.invites) {
      api.postMessage.buddiesRemoveInvite(id, {});
    }
    event.invites = {};
  }
}

/**
 * Set the score of a player from the party, in the current event
 */
export function buddiesAddPartyPlayerScore(
  state: MutableState,
  args: { id: string; score: number; now: number },
): void {
  const schedule = buddiesSchedule.getCurrentEvent(args.now);
  if (!schedule) return;
  const event = buddiesEnsureEvent(state, schedule.id);
  const player = event.party[args.id];
  if (!player) return;
  player.score += args.score;
  player.lastScoreAdded = (player.lastScoreAdded ?? 0) + args.score;

  // If grand prize just reached, cancel all pending local invites
  if (buddiesIsEventCompleted(state, args.now)) {
    event.invites = {};
  }
}

/**
 * Clear the last score added to the main player and the party
 */
export function buddiesClearLastScoreAdded(
  state: MutableState,
  now: number,
): void {
  const schedule = buddiesSchedule.getCurrentEvent(now);
  if (!schedule) return;
  const event = buddiesEnsureEvent(state, schedule.id);
  event.lastScoreAdded = 0;
  for (const id in event.party) {
    event.party[id].lastScoreAdded = 0;
  }
}

/**
 * Mark FTUE as seen for the current event
 */
export function buddiesMarkFTUESeen(state: MutableState, now: number): void {
  const schedule = buddiesSchedule.getCurrentEvent(now);
  if (!schedule) return;
  const event = buddiesEnsureEvent(state, schedule.id);
  event.ftueSeenAt = now;
}

/**
 * Claim event rewards if eligible. Returns the gem amount, or 0 if not claimable.
 */
export function buddiesClaimEventRewards(
  state: MutableState,
  eventId: string,
  now: number,
): number {
  const details = buddiesGetEventRewardDetails(state, eventId, now);
  if (!details?.canClaimRewards) return 0;

  const event = state.bakingBuddies?.events?.[eventId];
  if (event) event.rewardsClaimed = true;

  return details.totalReward;
}

/**
 * Add a player to the rejected suggestions list (dismissed or rejected invite).
 * Rejected suggestions are timestamped and retained for up to
 * `buddiesEventExpirationTime` after rejection, after which they are pruned
 * by `buddiesCleanupOldEvents`.
 */
export function buddiesRejectSuggestion(
  state: MutableState,
  args: { playerId: string; now: number },
): void {
  const buddies = buddiesEnsureState(state);
  if (!buddies.rejectedSuggestions) {
    buddies.rejectedSuggestions = {};
  }
  buddies.rejectedSuggestions[args.playerId] = args.now;
}

/**
 * Record a nudge sent to a party member.
 * Updates the recipient's lastNudgedAt and increments the sender's nudge count.
 */
export function buddiesRecordNudge(
  state: MutableState,
  args: { recipientId: string; now: number },
): void {
  const schedule = buddiesSchedule.getCurrentEvent(args.now);
  if (!schedule) return;
  const event = buddiesEnsureEvent(state, schedule.id);

  const player = event.party[args.recipientId];
  if (!player) return;

  player.lastNudgedAt = args.now;
  event.nudgesSentCount += 1;
}

/**
 * Acknowledge unseen party joins by updating lastSeenPartySize to current party size.
 * Returns the count of newly acknowledged joins (for the action to grant rewards).
 */
export function buddiesAcknowledgePartyJoins(
  state: MutableState,
  now: number,
): number {
  const schedule = buddiesSchedule.getCurrentEvent(now);
  if (!schedule) return 0;

  const event = buddiesEnsureEvent(state, schedule.id);
  const currentPartySize = Object.keys(event.party).length;
  const lastSeenSize = event.lastSeenPartySize ?? 0;

  // Clamp watermark down if party has shrunk so future joins are still counted
  if (currentPartySize < lastSeenSize) {
    event.lastSeenPartySize = currentPartySize;
  }

  const newJoins = buddiesGetUnseenPartyJoinCount(state, now);
  if (newJoins === 0) return 0;

  event.lastSeenPartySize = currentPartySize;
  return newJoins;
}

/**
 * Delete events that have expired
 */
export function buddiesCleanupOldEvents(
  state: MutableState,
  now: number,
): void {
  if (!state.bakingBuddies || !state.bakingBuddies.events) return;
  for (const id in state.bakingBuddies.events) {
    const event = buddiesSchedule.findEventByID(id);
    if (!event || event.start === 0) {
      // If somehow there is no event in the schedule, but there is an event in the state, delete it
      delete state.bakingBuddies.events[id];
      continue;
    }
    if (now > event.start + buddiesEventExpirationTime) {
      // Backfill the durable introduction marker before the record disappears.
      if (state.bakingBuddies.events[id].seenAt > 0) {
        state.bakingBuddies.introducedAt ??=
          state.bakingBuddies.events[id].seenAt;
      }
      delete state.bakingBuddies.events[id];
    }
  }

  // Prune rejected suggestions older than the event expiration time
  if (state.bakingBuddies.rejectedSuggestions) {
    for (const playerId in state.bakingBuddies.rejectedSuggestions) {
      const rejectedAt = state.bakingBuddies.rejectedSuggestions[playerId];
      if (now > rejectedAt + buddiesEventExpirationTime) {
        delete state.bakingBuddies.rejectedSuggestions[playerId];
      }
    }
  }
}
